import { isDefined } from "@clipboard-health/util-ts";
import { useGetWorkplaceEnabledConfigurations } from "@src/appV2/ExternalIntegrations/api/useGetWorkplaceEnabledConfigurations";
import { ExtendedExternalIntegrationTypes } from "@src/appV2/ExternalIntegrations/types";
import { useCredentialSharing } from "@src/appV2/Professionals/MyProfessionals/hooks/useCredentialSharing";
import {
  ChartingCredentialSoftware,
  type WorkerCredentialsAttributes,
} from "@src/appV2/WorkerCredentials/api/schema";
import { useGetDefaultChartingPassword } from "@src/appV2/WorkerCredentials/api/useGetDefaultChartingPassword";
import { useGetWorkerCredentials } from "@src/appV2/WorkerCredentials/api/useGetWorkerCredentials";
import { areCredentialsMissing } from "@src/appV2/WorkerCredentials/areCredentialsMissing";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useLocalStorage } from "react-use";

interface UseSetupShareCredentialsFormParams {
  workerId: string;
  workplaceId: string;
}

export function useSetupShareCredentialsForm(params: UseSetupShareCredentialsFormParams) {
  const { workplaceId, workerId } = params;
  const {
    data: workerCredentialsResponse,
    isLoading: isChartingCredentialsLoading,
    isSuccess: isChartingCredentialsSuccess,
    refetch: refetchChartingCredentials,
  } = useGetWorkerCredentials({
    filter: {
      workerIds: workerId,
      workplaceId,
    },
  });
  const {
    defaultChartingPassword,
    isLoading: isDefaultChartingPasswordLoading,
    isSuccess: isDefaultChartingPasswordSuccess,
  } = useGetDefaultChartingPassword({
    workplaceId,
  });

  const { isChartingAndTimeClock, isTimeClockOnly, isChartingOnly } = useCredentialSharing();
  const shouldShowTimeClockCredentials = isChartingAndTimeClock || isTimeClockOnly;
  const [enabledExternalConfiguration] = useGetWorkplaceEnabledConfigurations({ workplaceId });
  const timeclockCompliance = shouldShowTimeClockCredentials
    ? ExtendedExternalIntegrationTypes.TIMECLOCK_COMPLIANCE
    : undefined;

  const timeclockVendor = enabledExternalConfiguration ?? timeclockCompliance;

  const showCharting = isChartingAndTimeClock || isChartingOnly;
  const workerCredentials: Pick<
    WorkerCredentialsAttributes,
    "chartingCredentials" | "timeclockCredentials"
  > = workerCredentialsResponse?.data[0]?.attributes ?? {};
  const { chartingCredentials, timeclockCredentials } = workerCredentials;
  const [showPassword, setShowPassword] = useLocalStorage(
    "show-charting-credentials-password",
    false
  );
  const [showPin, setShowPin] = useLocalStorage("show-timeclock-credentials-pin", false);
  const [defaultChartingSoftware, setDefaultChartingSoftware] = useLocalStorage(
    "default-charting-software",
    ChartingCredentialSoftware.POINT_CLICK_CARE
  );
  const initialChartingPassword = chartingCredentials?.password ?? defaultChartingPassword ?? "";

  const missingInitialCredentials = areCredentialsMissing({
    isChartingAndTimeClock,
    isChartingOnly,
    isTimeClockOnly,
    credentials: { ...workerCredentials, workerId, workplaceId },
  });
  const [shouldUpdateDefaultChartingPassword, setShouldUpdateDefaultChartingPassword] =
    useState(false);
  // Update the default charting password checkbox once the data is loaded
  useEffect(() => {
    if (isChartingCredentialsSuccess && isDefaultChartingPasswordSuccess) {
      const shouldUseDefault =
        isDefined(defaultChartingPassword) &&
        !isEmpty(defaultChartingPassword) &&
        (isEmpty(chartingCredentials?.password) ||
          chartingCredentials?.password === defaultChartingPassword);

      setShouldUpdateDefaultChartingPassword(shouldUseDefault);
    }
  }, [
    chartingCredentials?.password,
    defaultChartingPassword,
    isChartingCredentialsSuccess,
    isDefaultChartingPasswordSuccess,
  ]);

  return {
    isLoading: isChartingCredentialsLoading || isDefaultChartingPasswordLoading,
    refetchChartingCredentials,
    timeclockVendor,
    showCharting,
    chartingCredentials,
    timeclockCredentials,
    defaultChartingSoftware,
    setDefaultChartingSoftware,
    initialChartingPassword,
    shouldUpdateDefaultChartingPassword,
    setShouldUpdateDefaultChartingPassword,
    showPassword,
    setShowPassword,
    showPin,
    setShowPin,
    missingInitialCredentials,
  };
}
